<script lang="ts">
	import { getTranslate } from "@utils/getTranslate.js";
	import { keyboardInset } from "@utils/keyboardInset.js";
	import { type Snippet, onMount } from "svelte";
	import type { HTMLDialogAttributes } from "svelte/elements";
	import { on } from "svelte/events";
	import plus from "@core/assets/icons/plus.svg?raw";
	import { createScrollLock } from "../../state/lockScroll.svelte.js";
	import Heading from "./Heading.svelte";
	import Icon from "./Icon.svelte";
	import translations from "./SideModal.translations.json" with { type: "json" };

	let {
		show = $bindable(false),
		/* eslint-disable prefer-const */
		class: customClass,
		children,
		heading,
		fullContentWidth,
		hideControls = false,
		...restProps
		/* eslint-enable prefer-const */
	}: HTMLDialogAttributes & {
		show: boolean;
		heading: string;
		fullContentWidth?: boolean;
		children?: Snippet;
		hideControls?: boolean;
	} = $props();

	const componentId = $props.id();
	let dialog = $state<HTMLDialogElement | null>(null);

	const translate = getTranslate(translations);

	$effect(() => {
		if (!dialog) {
			return;
		}

		if (show && !dialog.open) {
			dialog.showModal();
		}
		if (!show && dialog.open) {
			dialog.close();
		}
	});

	const scrollLock = createScrollLock();
	$effect(() => {
		scrollLock.locked = show;
	});

	function closeOnBackdrop(event: MouseEvent): void {
		if (!event.target) {
			return;
		}

		if (event.target === event.currentTarget) {
			show = false;
		}
	}

	// Not using onclick because svelte complains about accessibility (non-interactable elements with a mouse/keyboard event listener).
	onMount(() => dialog && on(dialog, "click", closeOnBackdrop));
</script>

<dialog
	bind:this={dialog}
	use:keyboardInset={show}
	{...restProps}
	class={[
		"backdrop:bg-fcrdarkgray/80 open:grid grid-cols-1 grid-rows-1 max-w-full w-190 min-w-[30vw] m-0 ml-auto h-dvh min-h-svh max-h-lvh bg-transparent outline-none overflow-hidden pb-[max(env(safe-area-inset-bottom,0px),var(--keyboard-inset,0px))]",
		customClass,
	]}
	onclose={(event: Event & { currentTarget: EventTarget & HTMLDialogElement }): void => {
		show = false;
		restProps.onclose?.(event);
	}}
	aria-labelledby="{componentId}-heading"
>
	<div>
		<div class="bg-white size-full relative flex flex-col overflow-auto overscroll-contain gap-1">
			{#if !hideControls}
				<div
					class="flex justify-between items-center px-4 py-3 border-b border-fcrlightgray-300 md:border-none md:pt-4 2xl:pt-6 md:pr-6 md:pl-12"
				>
					<Heading
						id="{componentId}-heading"
						level={2}
						text={heading}
						class="text-fcrdarkgray font-medium font-antipol leading-tighter text-5 lg:text-7.5 2xl:text-10"
					/>
					<button
						type="button"
						class={[
							"border-fcrgray-300 md:border size-12 2xl:size-18 flex items-center justify-center rounded-full",
							"[&:not(:disabled):hover]:border-fcrdarkgray",
							"[&:not(:disabled):hover:active]:scale-95 transition-transform duration-150 ease-in-out",
						]}
						onclick={(): void => {
							show = false;
						}}
						aria-label={translate("close")}
					>
						<Icon icon={plus} class="size-5 2xl:size-6 rotate-45" />
					</button>
				</div>
			{/if}
			<div
				class={["flex flex-col grow shrink overflow-y-auto overscroll-contain", !fullContentWidth && "px-4 md:px-12"]}
			>
				{#if show}
					{@render children?.()}
				{/if}
			</div>
		</div>
	</div>
</dialog>

<style>
	@keyframes appear {
		from {
			opacity: 0;
			pointer-events: none;
			transform: translateX(1rem);
		}

		to {
			opacity: 1;
			pointer-events: auto;
			transform: translateX(0);
		}
	}

	dialog {
		animation: appear 200ms ease-out forwards;
	}
</style>
